import {
  ChangeEvent,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from 'react'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import useUploadZipCFDIs from 'hooks/Invoices/CFDIs/useUploadZipCFDIs'
import { useAppSelector } from 'store/hooks'
import { ICFDIsZIPUploadContainerProps } from './CFDIsZIPUploadContainer'

interface IUseCFDIsZIPUploadContainer {
  fileReceived: File | null
  inputFile: MutableRefObject<HTMLInputElement | null>
  isUploadingZip: boolean
  handleReplaceFileWindow: () => void
  handleSelectFile: (event: ChangeEvent<HTMLInputElement>) => void
  handleRemoveFile: () => void
  handleUploadFiles: () => void
}

const useCFDIsZIPUploadContainer = (
  props: ICFDIsZIPUploadContainerProps
): IUseCFDIsZIPUploadContainer => {
  const { file, fireZipSizeErrorModal, handleCloseDrawer } = props

  const workspaceId = useAppSelector(selectActiveWorkspace)

  const [fileReceived, setFileReceived] = useState<File | null>(file)

  const inputFile = useRef<HTMLInputElement | null>(null)

  const { mutate: uploadZip, isLoading: isUploadingZip } =
    useUploadZipCFDIs(handleCloseDrawer)

  const handleCancelZipSizeError = useCallback(() => {
    if (inputFile && inputFile.current) {
      inputFile.current.value = ''
    }
    handleCloseDrawer()
  }, [inputFile, handleCloseDrawer])

  const handleReplaceFileWindow = useCallback(() => {
    if (inputFile !== null && inputFile.current !== null) {
      inputFile.current.click()
    }
  }, [inputFile])

  const handleSelectFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files: FileList | null = event.target.files
      if (files && files.length > 0) {
        const selectedZipFile = Array.from(files)[0]
        if (selectedZipFile.size < 10485760 + 1) {
          setFileReceived(selectedZipFile)
          return
        }

        fireZipSizeErrorModal(
          selectedZipFile.name,
          handleReplaceFileWindow,
          handleCancelZipSizeError
        )
      }
    },
    [fireZipSizeErrorModal, handleCancelZipSizeError, handleReplaceFileWindow]
  )

  const handleRemoveFile = useCallback(() => {
    if (fileReceived !== null) {
      setFileReceived(null)
      handleCloseDrawer()
    }
  }, [fileReceived, handleCloseDrawer])

  const handleUploadFiles = useCallback(() => {
    if (fileReceived !== null) {
      uploadZip({
        workspaceId: workspaceId,
        file: fileReceived,
      })
    }
  }, [workspaceId, fileReceived, uploadZip])

  return {
    fileReceived,
    inputFile,
    isUploadingZip,
    handleReplaceFileWindow,
    handleSelectFile,
    handleRemoveFile,
    handleUploadFiles,
  }
}

export default useCFDIsZIPUploadContainer
